import { Check, CheckCheck, Clock, AlertCircle, ImageIcon, Reply } from 'lucide-react';
import { formatWhatsAppTimestamp } from '@/lib/dateUtils';
import { MessageAttachment } from '@/components/whatsapp/MessageAttachment';
import { WhatsAppAudioPlayer } from './WhatsAppAudioPlayer';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { getWhatsAppErrorDescription } from '@/lib/whatsappErrors';
import { cn } from '@/lib/utils';
import type { Message, MessageAttachment as MsgAttachment } from '../hooks/useConversation';

interface MessageBubbleProps {
  message: Message;
  showTail?: boolean;
  grouped?: boolean;
  onReply?: (msg: Message) => void;
}

const QUOTE_REGEX = /^>\s?(.+?)(?:\n\n|\n)([\s\S]*)$/;

function parseQuote(body: string | null): { quoted: string | null; text: string } {
  if (!body) return { quoted: null, text: '' };
  const m = body.match(QUOTE_REGEX);
  if (!m) return { quoted: null, text: body };
  return { quoted: m[1].trim(), text: (m[2] || '').trim() };
}

const AUDIO_PLACEHOLDER_REGEX = /^(🎤\s*Mensagem de voz|🎵\s*Mensagem de áudio|\[Áudio\]|\[Audio\])$/i;

export function MessageBubble({ message, showTail = true, grouped = false, onReply }: MessageBubbleProps) {
  const isOutbound = message.direction === 'outbound';
  const timestamp = message.sent_at || message.received_at || message.created_at;

  // Internal note variant — distinct yellow card, never a chat bubble
  if (message.source === 'internal_note') {
    const author = getSenderName(message) || 'Usuário';
    return (
      <div className="mt-3 mb-1 px-1">
        <div className="relative rounded-md border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 shadow-sm">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <span className="text-[10.5px] font-semibold tracking-wide uppercase text-amber-700 dark:text-amber-300">
              Nota interna · {author}
            </span>
            <span className="text-[10.5px] text-amber-700/70 dark:text-amber-300/70 leading-none">
              {formatWhatsAppTimestamp(timestamp).time}
            </span>
          </div>
          <p className="text-[14px] leading-[19px] whitespace-pre-wrap [overflow-wrap:anywhere] text-amber-900 dark:text-amber-100">
            {message.body}
          </p>
        </div>
      </div>
    );
  }

  const { quoted, text } = parseQuote(message.body);

  const audioAttachment = message.message_attachments?.find(a => a.content_type?.startsWith('audio/'));
  const hasAudioMedia = !!audioAttachment
    || (message.media_mimetype?.startsWith('audio/') && !!message.media_storage_path);
  const bodyIsAudioPlaceholder = !!message.body && AUDIO_PLACEHOLDER_REGEX.test(message.body.trim());
  const isAudioAttachment = hasAudioMedia || (isOutbound && bodyIsAudioPlaceholder);
  const audioPending = isAudioAttachment && !hasAudioMedia;

  return (
    <div className={cn(
      'flex group',
      isOutbound ? 'justify-end' : 'justify-start',
      grouped ? 'mt-0.5' : 'mt-2'
    )}>
      {/* Reply button (hover) - left of received / right of sent */}
      {isOutbound && onReply && (
        <button
          onClick={() => onReply(message)}
          className="opacity-0 group-hover:opacity-100 transition-opacity self-center mr-1 p-1.5 rounded-full bg-wa-in dark:bg-wa-in shadow-sm hover:bg-wa-sidebar-active"
          title="Responder"
          type="button"
        >
          <Reply className="w-3.5 h-3.5 text-wa-text-muted" />
        </button>
      )}

      <div
        className={cn(
          'relative max-w-[75%] min-w-0 px-2 py-1.5 shadow-sm',
          isOutbound
            ? 'bg-wa-out text-wa-text rounded-lg'
            : 'bg-wa-in text-wa-text rounded-lg',
          // tail only on first message of group
          showTail && isOutbound && !grouped && 'wa-bubble-out-tail rounded-tr-none',
          showTail && !isOutbound && !grouped && 'wa-bubble-in-tail rounded-tl-none'
        )}
      >
        {/* Quoted reply preview */}
        {quoted && (
          <div className={cn(
            'mb-1 px-2 py-1.5 rounded border-l-[3px] bg-black/5 dark:bg-white/5',
            isOutbound ? 'border-wa-reply-out' : 'border-wa-reply-in'
          )}>
            <p className={cn(
              'text-[12px] font-semibold mb-0.5',
              isOutbound ? 'text-wa-reply-out' : 'text-wa-reply-in'
            )}>
              Mensagem original
            </p>
            <p className="text-[13px] text-wa-text-muted line-clamp-2 [overflow-wrap:anywhere]">
              {quoted}
            </p>
          </div>
        )}

        {/* Audio (replaces default attachment renderer for audio) */}
        {isAudioAttachment && (
          <WhatsAppAudioPlayer
            storagePath={audioAttachment?.storage_path || message.media_storage_path}
            externalUrl={(audioAttachment as any)?.external_url || null}
            isOutbound={isOutbound}
            pending={audioPending}
          />
        )}

        {/* Body text */}
        {text && !isAudioAttachment && !isMediaPlaceholder({ ...message, body: text }) && (
          <p className="text-[14.2px] leading-[19px] whitespace-pre-wrap [overflow-wrap:anywhere] text-wa-text pr-14">
            {text}
          </p>
        )}

        {/* Direct media (Twilio) - non-audio */}
        {message.media_storage_path && !isAudioAttachment && (
          <div className="mt-0.5">
            <MessageAttachment
              attachment={{
                id: message.id,
                file_name: message.media_filename || 'arquivo',
                content_type: message.media_mimetype || 'application/octet-stream',
                storage_path: message.media_storage_path,
                file_size: message.media_size_bytes || 0,
                created_at: message.created_at,
              }}
              isOutbound={isOutbound}
            />
          </div>
        )}

        {/* Attachments from message_attachments table */}
        {message.message_attachments && message.message_attachments.length > 0 && (
          <div className="mt-1 space-y-2">
            {message.message_attachments
              .filter(a => !a.content_type?.startsWith('audio/'))
              .map((att) => (
                <MessageAttachment key={att.id} attachment={att} isOutbound={isOutbound} />
              ))}
          </div>
        )}

        {/* Media indicator (no actual media) */}
        {(message.num_media ?? 0) > 0 &&
          !message.media_storage_path &&
          !(message.message_attachments && message.message_attachments.length > 0) && (
            <div className="flex items-center gap-1 mt-1 text-xs text-wa-text-muted">
              <ImageIcon className="w-3 h-3" />
              {message.num_media} anexo{(message.num_media ?? 0) > 1 ? 's' : ''}
            </div>
          )}

        {/* Timestamp + sender name + status (inline bottom-right) */}
        <div className="float-right flex items-center gap-1 ml-2 mt-0.5 -mb-0.5 select-none">
          {isOutbound && (() => {
            const senderName = getSenderName(message);
            if (!senderName) return null;
            return (
              <>
                <span
                  className="text-[10.5px] text-wa-text-muted leading-none max-w-[120px] truncate"
                  title={senderName}
                >
                  {senderName}
                </span>
                <span className="text-[10.5px] text-wa-text-muted leading-none">·</span>
              </>
            );
          })()}
          <span className="text-[10.5px] text-wa-text-muted leading-none">
            {formatWhatsAppTimestamp(timestamp).time}
          </span>
          {isOutbound && <StatusIcon status={message.status} errorCode={message.error_code} />}
        </div>
        <div className="clear-both" />
      </div>

      {/* Reply button for inbound (right of bubble) */}
      {!isOutbound && onReply && (
        <button
          onClick={() => onReply(message)}
          className="opacity-0 group-hover:opacity-100 transition-opacity self-center ml-1 p-1.5 rounded-full bg-wa-in dark:bg-wa-in shadow-sm hover:bg-wa-sidebar-active"
          title="Responder"
          type="button"
        >
          <Reply className="w-3.5 h-3.5 text-wa-text-muted" />
        </button>
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function getSenderName(msg: Message): string | null {
  const sb = (msg as any).sent_by as { display_name?: string | null; email?: string | null } | null | undefined;
  if (!sb) return null;
  const name = sb.display_name?.trim();
  if (name) return name;
  const email = sb.email?.trim();
  if (email) return email.split('@')[0];
  return null;
}

const MEDIA_PLACEHOLDERS = [
  '[Imagem]', '[Vídeo]', '[Áudio]', '[Documento]', '[Sticker]',
  '[Image]', '[Video]', '[Audio]', '[Document]',
  '🎵 Mensagem de áudio', '🎤 Mensagem de voz',
];

function isMediaPlaceholder(msg: Message): boolean {
  if (!msg.body) return false;
  const hasMedia = !!msg.media_storage_path ||
    (msg.message_attachments && msg.message_attachments.length > 0) ||
    (msg.num_media ?? 0) > 0;
  return hasMedia && MEDIA_PLACEHOLDERS.includes(msg.body.trim());
}

function StatusIcon({ status, errorCode }: { status: string | null; errorCode: string | null }) {
  if (status === 'read') return <CheckCheck className="w-3.5 h-3.5 text-wa-check" />;
  if (status === 'delivered') return <CheckCheck className="w-3.5 h-3.5 text-wa-text-muted" />;
  if (status === 'sent' || status === 'accepted') return <Check className="w-3.5 h-3.5 text-wa-text-muted" />;
  if (status === 'failed' || status === 'undelivered') {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertCircle className="w-3.5 h-3.5 text-destructive cursor-pointer" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-xs font-medium">
              {status === 'undelivered' ? 'Não entregue' : 'Falha no envio'}
              {errorCode && ` (Erro ${errorCode})`}
            </p>
            <p className="text-xs text-muted-foreground">
              {getWhatsAppErrorDescription(errorCode)}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return <Clock className="w-3 h-3 text-wa-text-muted" />;
}
